/**
 * Wave 3 · 22 #10 — "Where is it?": one part on one project from the request to the site.
 * Requested → Ordered → Expected → Delivered → In store → Issued to site, each with its quantity, who holds the
 * open step and for how long ("Carrier Gulf · 279 d"), the LPOs behind it and the activity, newest first.
 * GET /procurement/material-trace (quantities and dates only — Material access is enough).
 * Opened from a site request (⋯ › Where is it?), an LPO line's part number and Forecast's demand rows; the project
 * Material tab can open it the same way: <MaterialTracePanel open part="BE-GT20W.02" projectId={id} … />.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, CloudOff, FileText, FolderOpen, Minus, PackageCheck, Route, Truck, Warehouse } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { MissingText } from './SfUi';

const STEP_DOT = {
  done: 'border-status-green bg-status-green text-white',
  current: 'border-brand bg-brand text-brand-fg',
  todo: 'border-line bg-surface text-ink-4',
  skipped: 'border-line bg-surface-2 text-ink-4',
};
const EVENT_ICON = { requested: FileText, draft: FileText, ordered: FileText, delivered: Truck, issued: PackageCheck };

export function useMaterialTrace(part, projectId, open) {
  const [state, setState] = useState({ data: null, loading: false, error: '' });
  const load = useCallback(async () => {
    if (!part) return;
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const params = { part_number: part };
      if (projectId) params.project_id = projectId;
      const res = await api.get('/procurement/material-trace', { params });
      setState({ data: res.data, loading: false, error: '' });
    } catch (err) {
      setState({ data: null, loading: false, error: getErrorMessage(err, 'Could not trace this part') });
    }
  }, [part, projectId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  return { ...state, reload: load };
}

function Step({ step, last }) {
  const late = step.key === 'expected' && /late/.test(step.detail || '');
  return (
    <li className="relative grid grid-cols-[22px_minmax(0,1fr)_auto] gap-x-3 pb-4 last:pb-0" data-testid={`trace-step-${step.key}`} data-state={step.state}>
      {!last ? <span className="absolute left-[10px] top-[22px] h-[calc(100%-18px)] w-[2px] bg-[color:var(--line)]" aria-hidden="true" /> : null}
      <span className={cn('relative z-[1] mt-0.5 grid h-[22px] w-[22px] place-items-center rounded-full border-2', STEP_DOT[step.state] || STEP_DOT.todo, late && step.state === 'current' && 'border-status-red bg-status-red text-white')}>
        {step.state === 'done' ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> : step.state === 'skipped' ? <Minus className="h-3 w-3" aria-hidden="true" /> : null}
        <span className="sr-only">{step.state === 'done' ? 'Done' : step.state === 'current' ? 'In progress' : step.state === 'skipped' ? 'Skipped' : 'Not yet'}</span>
      </span>
      <div className="min-w-0">
        <p className={cn('text-[13.5px] font-semibold', step.state === 'todo' || step.state === 'skipped' ? 'text-ink-3' : 'text-ink')}>{step.label}</p>
        {step.detail ? <p className={cn('text-[12px]', late ? 'font-semibold text-status-red-ink' : 'text-ink-3')}>{step.detail}</p> : null}
        {step.holder && step.state === 'current' ? (
          <p className="mt-0.5 text-[12px] text-ink-2">
            With <b className="font-semibold text-ink">{step.holder}</b>{step.days !== null && step.days !== undefined ? <> · {formatNumber(step.days)} d</> : null}
          </p>
        ) : null}
      </div>
      <span className="pt-0.5 text-right text-[13px] font-bold tabular-nums text-ink">{step.value || ''}</span>
    </li>
  );
}

/** props: open, onOpenChange, part (part number), projectId (optional), projectName, description (shown while loading). */
export function MaterialTracePanel({ open, onOpenChange, part, projectId, projectName, description }) {
  const { data, loading, error, reload } = useMaterialTrace(part, projectId, open);
  const project = data?.project || (projectId ? { id: projectId, name: projectName } : null);
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      icon={Route}
      title={<span className="font-mono">{part || 'Part'}</span>}
      subtitle={[data?.description || description, project?.name || (projectId ? '' : 'All projects')].filter(Boolean).join(' · ')}
      testId="material-trace"
      footer={(
        <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px]">
          {project?.id ? (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/projects/${project.id}?tab=material`}><FolderOpen aria-hidden="true" />Open project material</Link>
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/inventory?tab=stock&search=${encodeURIComponent(part || '')}`}><Warehouse aria-hidden="true" />Stock</Link>
          </Button>
          <Button className="ml-auto" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </footer>
      )}
    >
      {error ? (
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't trace this part" text={error}
          action={<Button variant="outline" size="sm" onClick={reload}>Try again</Button>} />
      ) : !data || loading ? (
        <div className="space-y-3" aria-busy="true">
          <span className="ui-skeleton-shimmer block h-14 rounded-xl" />
          <span className="ui-skeleton-shimmer block h-64 rounded-xl" />
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-line bg-surface-2 px-3.5 py-3" data-testid="material-trace-headline">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Where it is now</p>
            <p className="mt-0.5 font-heading text-[16px] font-bold text-ink">{data.headline}</p>
            <p className="mt-1 text-[12px] text-ink-3">
              {[
                data.totals.boq ? `BOQ ${formatNumber(data.totals.boq)} ${data.unit}` : null,
                data.totals.requested ? `requested ${formatNumber(data.totals.requested)}` : null,
                data.totals.ordered ? `ordered ${formatNumber(data.totals.ordered)}` : null,
                `delivered ${formatNumber(data.totals.delivered)}`,
                `in store ${formatNumber(data.totals.on_hand)}`,
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          <SidePanelSection title="From request to site">
            <ol className="pt-1" data-testid="material-trace-steps">
              {data.steps.map((step, index) => <Step key={step.key} step={step} last={index === data.steps.length - 1} />)}
            </ol>
          </SidePanelSection>
          {data.lpos.length ? (
            <SidePanelSection title="LPOs with this part">
              <ul className="divide-y divide-line-2 rounded-xl border border-line">
                {data.lpos.map((lpo) => (
                  <li key={lpo.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 text-[12.5px]">
                    <Link to={`/procurement?tab=orders&lpo=${encodeURIComponent(lpo.lpo_number)}`} className="font-mono font-semibold text-brand-ink hover:underline">{lpo.lpo_number}</Link>
                    <StatusPill status={lpo.draft ? lpo.status : lpo.remaining > 0 ? 'Open' : 'Delivered'} tone={lpo.draft ? 'gray' : lpo.remaining > 0 ? 'blue' : 'navy'} size="sm" />
                    <span className="text-ink-3">{lpo.supplier || <MissingText>Supplier missing</MissingText>}</span>
                    <span className="ml-auto tabular-nums text-ink-2">{formatNumber(lpo.ordered - lpo.remaining)}/{formatNumber(lpo.ordered)} delivered</span>
                    {lpo.expected ? (
                      <span className="w-full text-[12px] text-ink-3">
                        Expected {formatDate(lpo.expected)}
                        {lpo.original_expected ? <> · first promised {formatDate(lpo.original_expected)}</> : null}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </SidePanelSection>
          ) : null}
          <SidePanelSection title="Activity" collapsible defaultOpen={data.events.length <= 6}>
            {data.events.length ? (
              <ul className="space-y-2.5" data-testid="material-trace-events">
                {data.events.map((event, index) => {
                  const Icon = EVENT_ICON[event.kind] || FileText;
                  return (
                    <li key={`${event.kind}-${index}`} className="grid grid-cols-[16px_minmax(0,1fr)_auto] gap-x-2.5 text-[12.5px]">
                      <Icon className="mt-0.5 h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block text-ink">{event.title}</span>
                        {event.sub ? <span className="block truncate text-[12px] text-ink-3">{event.sub}</span> : null}
                      </span>
                      <span className="whitespace-nowrap text-[12px] text-ink-3">{event.day ? formatDate(event.day, { year: 'auto' }) : '—'}</span>
                    </li>
                  );
                })}
              </ul>
            ) : <p className="text-[12.5px] text-ink-3">Nothing recorded for this part yet.</p>}
          </SidePanelSection>
        </>
      )}
    </SidePanel>
  );
}
